/**
 * Operations Monitor Service
 * Handles real-time operations monitor data fetching
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { generateMockOperationsMonitorData } from '../mock/mockOperationsMonitor';
import { delay } from '../mock/mockData';
import type { OperationsMonitorData, ReportFilters } from '../../types/reports';

class OperationsMonitorService {
  /**
   * Get operations monitor data
   */
  static async getOperationsMonitorData(
    filters?: ReportFilters
  ): Promise<OperationsMonitorData> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY / 2);
      return generateMockOperationsMonitorData();
    }

    const params: Record<string, string> = {};
    if (filters?.dateRange) params['DateRange'] = filters.dateRange;
    if (filters?.region) params['Region'] = filters.region;
    if (filters?.serviceType) params['ServiceType'] = filters.serviceType;
    if (filters?.priority) params['Priority'] = filters.priority;
    if (filters?.category && filters.category !== 'All Categories') params['Category'] = filters.category;
    if (filters?.ghostTier && filters.ghostTier !== 'All Tiers') params['GhostTier'] = filters.ghostTier;
    if (filters?.missionType && filters.missionType !== 'All Types') params['MissionType'] = filters.missionType;

    if (filters?.startDate) {
      params['StartDate'] = filters.startDate instanceof Date
        ? filters.startDate.toISOString()
        : filters.startDate;
    }
    if (filters?.endDate) {
      params['EndDate'] = filters.endDate instanceof Date
        ? filters.endDate.toISOString()
        : filters.endDate;
    }

    const response = await apiService.get<{
      isSuccess: boolean;
      data: OperationsMonitorData;
      message?: string;
    }>('/admin/reports/operations-monitor', { params });

    if (!response.isSuccess || !response.data) {
      throw new Error(response.message || 'Failed to fetch operations monitor data');
    }

    const data = response.data;

    // Ensure lastUpdated is a Date object
    if (data.lastUpdated && typeof data.lastUpdated === 'string') {
      data.lastUpdated = new Date(data.lastUpdated);
    }

    return data;
  }
}

export { OperationsMonitorService };

